import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AppContextService, ViewContextSnapshot } from './app-context.service';
import { CacheService } from './cache.service';

interface WorkspaceVisit {
  id: string;
  url: string;
  previous?: string;
  next?: string;
  snapshot: ViewContextSnapshot;
  returnTo?: { visitId: string; label: string };
}

const HISTORY_KEY = 'brWorkspaceVisit';
const HISTORY_CACHE = 'workspace-navigation';
const HISTORY_LIMIT = 80;

/** An adapter over Angular/browser history. Pages own view state; CacheService
 * owns account lifetime. History stores opaque visit IDs, never record payloads. */
@Injectable({ providedIn: 'root' })
export class WorkspaceNavigationService implements OnDestroy {
  private readonly ready = new BehaviorSubject(true);
  /** Route adapters may normalize query parameters only after owner restoration. */
  readonly viewReady$ = this.ready.asObservable();
  private readonly subscriptions = new Subscription();
  private current?: WorkspaceVisit;
  private restored?: WorkspaceVisit;
  private replace = false;
  private skip = false;
  private outsideHistory = false;
  private navigating = false;
  private started = false;
  private restoreTimer?: ReturnType<typeof setTimeout>;
  private routeContext?: Subscription;
  private requestedReturn?: WorkspaceVisit['returnTo'];

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly context: AppContextService,
    private readonly cache: CacheService,
  ) {}

  get canBack(): boolean {
    return !this.navigating && !!this.current?.previous && !!this.visit(this.current.previous);
  }
  get canForward(): boolean {
    return !this.navigating && !!this.current?.next && !!this.visit(this.current.next);
  }
  back(): void { if (this.canBack) this.location.back(); }
  forward(): void { if (this.canForward) this.location.forward(); }

  /** Explicit record origins reuse this account's visit graph, never guessed URLs. */
  sourceState(label: string): Record<string, unknown> {
    return this.current ? { workspaceReturn: { visitId: this.current.id, label } } : {};
  }
  get returnState(): Record<string, unknown> {
    return this.returnDistance ? { workspaceReturn: this.current!.returnTo } : {};
  }
  get returnLabel(): string | null { return this.returnDistance ? this.current!.returnTo!.label : null; }
  returnToSource(): boolean {
    const distance = this.returnDistance;
    if (!distance || this.navigating) return false;
    this.location.historyGo(-distance);
    return true;
  }
  private get returnDistance(): number {
    const target = this.current?.returnTo;
    if (!target || !this.visit(target.visitId)) return 0;
    let previous = this.current?.previous;
    for (let distance = 1; previous && distance <= HISTORY_LIMIT; distance++) {
      if (previous === target.visitId) return distance;
      previous = this.visit(previous)?.previous;
    }
    return 0;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.publishRoute();
    // A shell can mount after initial navigation (e.g. authentication restore).
    if (this.router.navigated && isWorkspaceUrl(this.router.url)) {
      const state = this.location.getState() as Record<string, unknown>;
      const id = state?.[HISTORY_KEY];
      this.current = typeof id === 'string' ? this.visit(id) : undefined;
      this.current ??= { id: crypto.randomUUID(), url: this.router.url, snapshot: {} };
      this.save(this.current);
      this.location.replaceState(this.location.path(), '', { ...state, [HISTORY_KEY]: this.current.id });
    }
    this.subscriptions.add(this.cache.sessionChanged$.subscribe(() => {
      clearTimeout(this.restoreTimer);
      this.current = this.restored = undefined;
      this.context.clearRestore();
      this.navigating = false;
      this.ready.next(true);
      this.publishRoute();
    }));
    this.subscriptions.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        clearTimeout(this.restoreTimer);
        if (this.current) {
          this.current.snapshot = this.context.capture();
          this.save(this.current);
        }
        this.context.clearRestore();
        this.navigating = true;
        this.ready.next(false);
        const navigation = this.router.getCurrentNavigation();
        const requested = navigation?.extras.state?.['workspaceReturn'];
        this.requestedReturn = requested && typeof requested.visitId === 'string' && typeof requested.label === 'string'
          && this.visit(requested.visitId) ? { visitId: requested.visitId, label: requested.label } : undefined;
        this.replace = navigation?.extras.replaceUrl ?? false;
        this.skip = navigation?.extras.skipLocationChange ?? false;
        const id = event.restoredState?.[HISTORY_KEY];
        this.restored = typeof id === 'string' ? this.visit(id) : undefined;
        // Native Back can reach a visit from another account or before this
        // app loaded. Treat it as a boundary, not a new forward navigation.
        this.outsideHistory = event.navigationTrigger !== 'imperative' && !this.restored;
      } else if (event instanceof NavigationEnd) {
        this.navigating = false;
        if (this.outsideHistory) this.current = undefined;
        const url = event.urlAfterRedirects;
        if (!isWorkspaceUrl(url)) {
          this.current = this.restored = undefined;
          this.routeContext?.unsubscribe();
          this.routeContext = undefined;
          this.ready.next(true);
          return;
        }
        if (this.restored?.url === url) {
          this.current = this.restored;
        } else if ((this.replace || this.skip) && this.current) {
          this.current.url = url;
          this.current.snapshot = {};
        } else {
          const prior = this.current;
          const next: WorkspaceVisit = { id: crypto.randomUUID(), url, previous: prior?.id, snapshot: {}, returnTo: this.requestedReturn };
          if (prior) {
            prior.next = next.id;
            this.save(prior);
          }
          this.current = next;
        }
        this.save(this.current);
        if (!this.skip) this.location.replaceState(this.location.path(), '', {
          ...(this.location.getState() as Record<string, unknown>), [HISTORY_KEY]: this.current.id,
        });
        this.publishRoute();
        const snapshot = this.restored?.url === url ? this.restored.snapshot : null;
        this.restored = undefined;
        // Let the router mount the page and the shell choose its layout first.
        // A canceled navigation never reaches this restoration boundary.
        if (snapshot) {
          this.navigating = true;
          this.restoreTimer = setTimeout(() => {
            this.context.restore(snapshot);
            this.navigating = false;
            this.ready.next(true);
          });
        } else this.ready.next(true);
      } else if (event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped) {
        this.navigating = false;
        this.restored = undefined;
        this.context.clearRestore();
        this.ready.next(true);
      }
    }));
  }

  private publishRoute(): void {
    this.routeContext?.unsubscribe();
    this.routeContext = this.context.registerView({
      key: 'workspace', name: 'Current Workspace', priority: -100,
      read: () => this.routeState(),
      describe: state => state ? JSON.stringify(state) : null,
    });
  }

  private routeState(): Record<string, unknown> | null {
    if (!isWorkspaceUrl(this.router.url)) return null;
    const pages: string[] = [];
    const records: Record<string, string> = {};
    let node: ActivatedRouteSnapshot | undefined = this.router.routerState.snapshot.root;
    while (node) {
      const title = node.routeConfig?.data?.['title'];
      if (typeof title === 'string') pages.push(title);
      // Dynamic IDs describe the current resource; tokens and arbitrary query
      // strings do not enter context. Forms must opt in with safe metadata.
      for (const key of ['id', 'workflowKey']) {
        const value = node.paramMap.get(key);
        if (value) records[key] = value;
      }
      node = node.firstChild ?? undefined;
    }
    const query = this.router.routerState.snapshot.root.queryParamMap;
    const view: Record<string, string[]> = {};
    for (const key of ['tab', 'feature', 'group', 'column']) {
      const values = query.getAll(key);
      if (values.length) view[key] = values;
    }
    return { path: this.router.url.split('?')[0].split('#')[0],
      pages: pages.length ? pages : ['Map'], records, view };
  }

  private visit(id: string): WorkspaceVisit | undefined { return this.cache.get(HISTORY_CACHE, id); }
  private save(visit: WorkspaceVisit): void { this.cache.set(HISTORY_CACHE, visit.id, visit, HISTORY_LIMIT); }

  ngOnDestroy(): void {
    clearTimeout(this.restoreTimer);
    this.routeContext?.unsubscribe();
    this.subscriptions.unsubscribe();
    this.ready.complete();
  }
}

function isWorkspaceUrl(url: string): boolean {
  const path = url.split('?')[0];
  return !path.startsWith('/sign/') && path !== '/reset-password';
}
